import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { US_STATES, type SelectorItem } from '@/constants';

interface MultiSelectorProps {
  value: string;
  onChange: (value: string) => void;
  items?: readonly SelectorItem[];
  className?: string;
  disabled?: boolean;
}

export function MultiSelector({
  value,
  onChange,
  items = US_STATES,
  className,
  disabled
}: MultiSelectorProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="flex flex-col space-y-1.5">
      <Popover open={disabled ? false : open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className={cn('bg-[hsl(var(--input-background))]', className)}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select"
            className="w-full justify-between cursor-pointer"
            disabled={disabled}
          >
            {value ? items.find((item) => item.value === value)?.label : 'Select...'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command>
            <CommandInput placeholder="Search..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem
                    key={item.value}
                    value={item.label}
                    className="cursor-pointer"
                    onSelect={() => {
                      if (!disabled) {
                        onChange(item.value === value ? '' : item.value);
                        setOpen(false);
                      }
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === item.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
